import { type DataLabel, dataLabel } from '@/i18n/dataLabels';

import { FISH_TYPE_SHORT_NAMES } from './types';

/**
 * What a species does differently, keyed on `fishTypes.shortName`.
 *
 * The identity always comes from the table, and a species with no entry below behaves exactly
 * like the default. The default is salmon: every field in `DEFAULT_TRAITS` is what salmon does,
 * and trout is the only species that departs from it today.
 *
 * The point of the indirection is that species-conditional behaviour must never be a binary
 * comparison at the call site. `fishType === 'salmon'` reads fine and is wrong the moment a
 * third species exists: it hands every species added later whatever salmon is not. That is
 * exactly what happened to coho, which lost the wound column, the wound cell and the wound
 * average before this existed. Adding behaviour for a new species is an entry here; until
 * someone adds one, the new species is a salmon.
 *
 * The backend has its own copy of this idea in `v2/shared/species/species-traits.ts`, declaring
 * the traits the API consumes. Each side lists only what it reads, so adding a trait means
 * deciding which side needs it rather than keeping two identical files in step.
 */
export interface SpeciesTraits {
    /** Wounds are scored for this species, so the column, cells and average apply to it. */
    woundScoring: boolean;
    /**
     * Translation key for the footnote explaining why a pen shows no wound rate. Null when the
     * species has wound scoring and so needs no explanation.
     */
    missingWoundsNoteKey: string | null;
    /** Which set of maturation score descriptions this species uses. */
    maturationCopy: 'default' | 'rainbowTrout';
    /**
     * The cage dashboard shows the harvest downgrade estimate. The model is fitted for salmon,
     * so a species it does not suit opts out here.
     */
    dashboardDowngradeEstimate: boolean;
    /**
     * The wound insight pages show the downgrade estimate. Separate from the dashboard trait on
     * purpose: a model that later suits the species for the dashboard does not bring the estimate
     * back to wound insight, where wounds may not drive its downgrades at all.
     */
    woundInsightDowngradeEstimate: boolean;
}

const DEFAULT_TRAITS: SpeciesTraits = {
    woundScoring: true,
    missingWoundsNoteKey: null,
    maturationCopy: 'default',
    dashboardDowngradeEstimate: true,
    woundInsightDowngradeEstimate: true,
};

const TRAITS_BY_SHORT_NAME: Record<string, Partial<SpeciesTraits>> = {
    [FISH_TYPE_SHORT_NAMES.TROUT]: {
        woundScoring: false,
        missingWoundsNoteKey: 'organisms.locationOverviewTable.noWoundsForTrout',
        maturationCopy: 'rainbowTrout',
        dashboardDowngradeEstimate: false,
        woundInsightDowngradeEstimate: false,
    },
};

export function speciesTraits(shortName?: string | null): SpeciesTraits {
    const overrides = shortName ? TRAITS_BY_SHORT_NAME[shortName] : undefined;

    return { ...DEFAULT_TRAITS, ...overrides };
}

/**
 * Species a reader already assumes, keyed on `fishTypes.shortName`.
 *
 * Salmon is the overwhelming majority of pens, so naming it beside every pen title is noise
 * that trains the eye to skip the label — which is the one thing it must not do on the rare pen
 * holding something else.
 *
 * This list is inverted relative to `TRAITS_BY_SHORT_NAME` on purpose, and it is not a trait
 * for that reason: a trait defaults to salmon's behaviour, and defaulting a species nobody has
 * written an entry for to "assumed" would hide exactly the label a reader needs. So a species
 * inserted into the database is labelled until someone decides it is common enough not to be.
 */
const ASSUMED_SHORT_NAMES: string[] = [FISH_TYPE_SHORT_NAMES.SALMON];

/**
 * The translation key for a pen's species, or null when there is nothing worth saying.
 *
 * Keyed on the stored `fishTypes.name`, the same key space as the location labels in
 * `@/domain/client`, with the stored name as the fallback, so a species with no entry in a
 * locale shows its stored name rather than a missing-key string.
 */
export function penSpeciesLabelKey(
    fishType?: { name: string; shortName: string } | null
): DataLabel | null {
    if (!fishType || ASSUMED_SHORT_NAMES.includes(fishType.shortName)) {
        return null;
    }

    return dataLabel('fishTypes', fishType.name);
}

/** Just the part of a pen these read, so an `OverviewCage` and a bare fixture both satisfy it. */
interface PenSpecies {
    fishType: { shortName: string } | null;
}

/** Whether any of these pens is a species wounds are scored for. Drives the column and average. */
export function hasWoundScoringPen(pens: PenSpecies[]): boolean {
    return pens.some((pen) => speciesTraits(pen.fishType?.shortName).woundScoring);
}

/**
 * The footnote to show under a pen table, or null when every pen has wound scoring.
 *
 * Takes the first note any pen asks for. With one species lacking wound scoring today that is
 * unambiguous; a second one would want the notes concatenated, which is a decision for whoever
 * adds it rather than a guess made here.
 */
export function missingWoundsNoteKey(pens: PenSpecies[]): string | null {
    for (const pen of pens) {
        const key = speciesTraits(pen.fishType?.shortName).missingWoundsNoteKey;
        if (key) return key;
    }

    return null;
}
